import type { PaginationMeta } from "@nasam/kernel";
import type {
  SupervisorAssignmentDetail,
  SupervisorAssignmentHistoryItem,
  SupervisorAssignmentListItem,
  SupervisorAssignmentsRepository,
} from "../domain";
import { assetOptionFixtures } from "../testing/assetOptionFixtures";
import {
  supervisorAssignmentFixtures,
  type MockSupervisorAssignmentRow,
} from "../testing/supervisorAssignmentFixtures";

/**
 * In-memory stand-in for every Supervisor Assignment operation -- used by
 * tests and available as a manual per-operation USE_MOCK fallback (see
 * fleetMonitoringFeature.ts); none of it runs in production, where every
 * method is wired to createSupervisorAssignmentsHttpRepository instead.
 */
const MOCK_LATENCY_MS = 300;

function delay(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));
}

function toListItem(row: MockSupervisorAssignmentRow): SupervisorAssignmentListItem {
  return {
    id: row.supervisor.id,
    supervisor: row.supervisor,
    assignedAssetsCount: row.assets.length,
    updatedAt: row.updatedAt,
  };
}

function toDetail(row: MockSupervisorAssignmentRow): SupervisorAssignmentDetail {
  return { supervisor: row.supervisor, assets: row.assets };
}

function includes(haystack: string, needle: string): boolean {
  return haystack.toLocaleLowerCase().includes(needle.toLocaleLowerCase());
}

function matchesKeyword(row: SupervisorAssignmentListItem, keyword: string) {
  return (
    includes(row.supervisor.nameEn, keyword) ||
    includes(row.supervisor.nameAr, keyword) ||
    includes(String(row.assignedAssetsCount), keyword)
  );
}

function pageMeta(
  page: number,
  limit: number,
  totalCount: number,
): PaginationMeta {
  const totalPages = Math.ceil(totalCount / limit);
  const pageUrl = (target: number) => `?page=${target}&limit=${limit}`;
  return {
    totalCount,
    currentPage: page,
    totalPages,
    firstPageUrl: pageUrl(1),
    lastPageUrl: pageUrl(Math.max(1, totalPages)),
    nextPageUrl: page < totalPages ? pageUrl(page + 1) : null,
    previousPageUrl: page > 1 ? pageUrl(page - 1) : null,
  };
}

export function createSupervisorAssignmentsMockRepository(): SupervisorAssignmentsRepository {
  let rows: readonly MockSupervisorAssignmentRow[] = supervisorAssignmentFixtures;

  // `${supervisorId}:${assetId}` pairs already assigned -- seeded from the
  // fixtures themselves, so duplicate detection works against the seed data
  // too, not only assignments created through `create()` in this session.
  const assignedPairs = new Set<string>(
    rows.flatMap((row) =>
      row.assets.map((asset) => `${row.supervisor.id}:${asset.id}`),
    ),
  );

  return {
    async list(query = {}) {
      await delay();

      const page = query.page ?? 1;
      const limit = query.limit ?? 10;
      const keyword = query.search?.trim();
      const from =
        query.updatedFrom === undefined ? undefined : Date.parse(query.updatedFrom);
      const to =
        query.updatedTo === undefined ? undefined : Date.parse(query.updatedTo);
      // Defaults to desc -- most-recently-updated first, same convention as
      // the sensor assignments list.
      const direction = query.sort === "asc" ? 1 : -1;
      const sortBy = query.sortBy ?? "updatedAt";

      const filtered = rows
        .map(toListItem)
        .filter(
          (row) =>
            (!keyword || matchesKeyword(row, keyword)) &&
            (from === undefined || Date.parse(row.updatedAt) >= from) &&
            (to === undefined || Date.parse(row.updatedAt) <= to),
        )
        .toSorted((a, b) => {
          if (sortBy === "supervisorName") {
            return a.supervisor.nameEn.localeCompare(b.supervisor.nameEn) * direction;
          }
          if (sortBy === "assignedAssetsCount") {
            return (a.assignedAssetsCount - b.assignedAssetsCount) * direction;
          }
          return (Date.parse(a.updatedAt) - Date.parse(b.updatedAt)) * direction;
        });

      return {
        data: filtered.slice((page - 1) * limit, page * limit),
        meta: pageMeta(page, limit, filtered.length),
      };
    },

    async listEligibleSupervisors() {
      await delay();
      return rows.map((row) => row.supervisor);
    },

    async listAssignableAssets(branchId) {
      await delay();
      return assetOptionFixtures.filter(
        (asset) => branchId === undefined || asset.branch.id === branchId,
      );
    },

    async getById(supervisorId) {
      await delay();
      const row = rows.find((candidate) => candidate.supervisor.id === supervisorId);
      if (row === undefined) {
        throw new Error(`Unknown supervisor id: ${supervisorId}`);
      }
      return toDetail(row);
    },

    async create(input) {
      await delay();

      const assets = input.assetIds.map((id) => {
        const option = assetOptionFixtures.find((candidate) => candidate.id === id);
        if (option === undefined) throw new Error(`Unknown asset id: ${id}`);
        return { id: option.id, nameEn: option.nameEn, nameAr: option.nameAr };
      });

      // All-or-nothing, matching the real endpoint's transactional 409.
      const duplicate = assets.find((asset) =>
        assignedPairs.has(`${input.supervisorId}:${asset.id}`),
      );
      if (duplicate !== undefined) {
        throw new Error("The selected Supervisor is already assigned to this Asset.");
      }

      const now = new Date().toISOString();
      for (const asset of assets) assignedPairs.add(`${input.supervisorId}:${asset.id}`);

      const existingIndex = rows.findIndex(
        (row) => row.supervisor.id === input.supervisorId,
      );
      if (existingIndex === -1) {
        // `create()` only runs against the real HTTP repository in
        // production (see fleetMonitoringFeature.ts) -- there's no name to
        // look up for a brand-new supervisor here, only an id. Keeps this
        // path usable for direct tests rather than throwing.
        rows = [
          ...rows,
          {
            supervisor: {
              id: input.supervisorId,
              nameEn: input.supervisorId,
              nameAr: input.supervisorId,
              username: input.supervisorId,
            },
            assets,
            updatedAt: now,
          },
        ];
      } else {
        rows = rows.map((row, index) =>
          index === existingIndex
            ? { ...row, assets: [...row.assets, ...assets], updatedAt: now }
            : row,
        );
      }

      return {
        assignments: assets.map((asset) => ({
          id: `mock-assignment-${asset.id}`,
          assetId: asset.id,
          assignedAt: now,
        })),
      };
    },

    // Duplicate detection is per (supervisor, asset) pair, not per asset --
    // the same physical asset can be actively held by several supervisors
    // at once (see assetsFor's own comment in supervisorAssignmentFixtures).
    // A full replace of *this* supervisor's own selection can't collide
    // with itself (assetIds is already a unique array), so there's nothing
    // left to conflict-check here, unlike create's own-pair duplicate test.
    async update(input) {
      await delay();

      const assets = input.assetIds.map((id) => {
        const option = assetOptionFixtures.find((candidate) => candidate.id === id);
        if (option === undefined) throw new Error(`Unknown asset id: ${id}`);
        return { id: option.id, nameEn: option.nameEn, nameAr: option.nameAr };
      });

      const existingIndex = rows.findIndex(
        (row) => row.supervisor.id === input.supervisorId,
      );
      if (existingIndex === -1) {
        throw new Error(`Unknown supervisor id: ${input.supervisorId}`);
      }

      for (const asset of rows[existingIndex]!.assets) {
        assignedPairs.delete(`${input.supervisorId}:${asset.id}`);
      }
      for (const asset of assets) {
        assignedPairs.add(`${input.supervisorId}:${asset.id}`);
      }

      const now = new Date().toISOString();
      rows = rows.map((row, index) =>
        index === existingIndex ? { ...row, assets, updatedAt: now } : row,
      );

      return {
        assignments: assets.map((asset) => ({
          id: `mock-assignment-${asset.id}`,
          assetId: asset.id,
          assignedAt: now,
        })),
      };
    },

    async delete(id) {
      await delay();
      const row = rows.find((candidate) => candidate.supervisor.id === id);
      if (row === undefined) throw new Error(`Unknown supervisor assignment id: ${id}`);
      for (const asset of row.assets) {
        assignedPairs.delete(`${row.supervisor.id}:${asset.id}`);
      }
      rows = rows.filter((candidate) => candidate.supervisor.id !== id);
      return { responseMessage: "Supervisor assignment removed successfully." };
    },

    // The mock only tracks current state, not a change log, so every
    // currently-active pair reads as a single "Created" entry -- there's no
    // way to synthesize Updated/Removed rows from an in-memory snapshot.
    async listHistory(query = {}) {
      await delay();

      const page = query.page ?? 1;
      const limit = query.limit ?? 10;
      const keyword = query.search?.trim();
      const direction = query.sort === "asc" ? 1 : -1;
      const sortBy = query.sortBy ?? "actionAt";
      const assignedFrom =
        query.fromAssignedAt === undefined ? undefined : Date.parse(query.fromAssignedAt);
      const assignedTo =
        query.toAssignedAt === undefined ? undefined : Date.parse(query.toAssignedAt);
      const actionFrom =
        query.fromActionAt === undefined ? undefined : Date.parse(query.fromActionAt);
      const actionTo =
        query.toActionAt === undefined ? undefined : Date.parse(query.toActionAt);

      const items: SupervisorAssignmentHistoryItem[] = rows.flatMap((row) =>
        row.assets.map((asset) => ({
          id: `${row.supervisor.id}:${asset.id}`,
          supervisorNameEn: row.supervisor.nameEn,
          supervisorNameAr: row.supervisor.nameAr,
          assetNameEn: asset.nameEn,
          assetNameAr: asset.nameAr,
          assignedByName: "System",
          action: "Created",
          assignedAt: row.updatedAt,
          actionAt: row.updatedAt,
        })),
      );

      const filtered = items
        .filter((item) => {
          const assignedTime = Date.parse(item.assignedAt);
          const actionTime = Date.parse(item.actionAt);
          return (
            (!keyword ||
              includes(item.supervisorNameEn, keyword) ||
              includes(item.supervisorNameAr, keyword) ||
              includes(item.assetNameEn, keyword) ||
              includes(item.assetNameAr, keyword) ||
              includes(item.assignedByName, keyword) ||
              includes(item.action, keyword)) &&
            (query.action === undefined || item.action === query.action) &&
            (assignedFrom === undefined || assignedTime >= assignedFrom) &&
            (assignedTo === undefined || assignedTime <= assignedTo) &&
            (actionFrom === undefined || actionTime >= actionFrom) &&
            (actionTo === undefined || actionTime <= actionTo)
          );
        })
        .toSorted((a, b) => {
          if (sortBy === "supervisorName") {
            return a.supervisorNameEn.localeCompare(b.supervisorNameEn) * direction;
          }
          if (sortBy === "assetName") {
            return a.assetNameEn.localeCompare(b.assetNameEn) * direction;
          }
          if (sortBy === "assignedAt") {
            return (Date.parse(a.assignedAt) - Date.parse(b.assignedAt)) * direction;
          }
          if (sortBy === "action") {
            return a.action.localeCompare(b.action) * direction;
          }
          return (Date.parse(a.actionAt) - Date.parse(b.actionAt)) * direction;
        });

      return {
        data: filtered.slice((page - 1) * limit, page * limit),
        meta: pageMeta(page, limit, filtered.length),
      };
    },
  };
}
